import { useApolloClient } from "@apollo/client/react";
import { Session } from "@ory/client-fetch";
import { useEffect, useRef, useState } from "react";
import { CompleteStaffRegistrationDocument } from "./CompleteStaffRegistration.generated";

// Kratos schemas provisioned through SSO. connect-api doesn't recognize these
// identities until completeStaffRegistration links them to a staff record.
const SSO_SCHEMA_IDS = ["oidc", "ameeliostaff"];

export type RegistrationStatus = "pending" | "done" | "failed";

// completeStaffRegistration creates or links the staff record on first SSO
// sign-in, and just returns the linked record on every sign-in after that.
export default function useRegistrationStatus(
  orySession: Session | undefined,
  hasToken: boolean,
  reportError: (error: unknown) => void
): RegistrationStatus {
  const client = useApolloClient();
  const schemaId = orySession?.identity?.schema_id;
  const isSsoIdentity = !!schemaId && SSO_SCHEMA_IDS.includes(schemaId);
  const attempted = useRef(false);
  const [status, setStatus] = useState<RegistrationStatus>("pending");

  // Complete pending SSO registration before the app renders, so connect-api
  // can resolve the current user.
  useEffect(() => {
    if (attempted.current || !isSsoIdentity || hasToken) return;
    attempted.current = true;
    client
      .mutate({ mutation: CompleteStaffRegistrationDocument })
      .then(() => setStatus("done"))
      .catch((e: unknown) => {
        reportError(e);
        setStatus("failed");
      });
  }, [client, isSsoIdentity, hasToken, reportError]);

  // Derived rather than stored: isSsoIdentity is false while the Ory session
  // is still loading, so storing "done" for it would stick once the session
  // arrives and let the app render before registration finishes. A Malan
  // token takes precedence in connect-api, so it needs no registration.
  if (!isSsoIdentity || hasToken) return "done";
  return status;
}
